import apiClient from './index'
import {
  API_BULK_METADATA_TIMEOUT_MS,
  API_FILE_UPLOAD_TIMEOUT_MS,
  API_LONG_RUNNING_TIMEOUT_MS,
  API_SAP_TIMEOUT_MS,
} from '@/api/timeouts'
import {
  ApiResponse,
  Approval,
  UpdateApprovalRequest,
  BulkUpdateRequest,
  SapDocument,
  DashboardData,
  Statistics,
  DocumentSummary,
  User,
  FileUploadResponse,
  DocumentType,
  SapInfo,
  BulkPodJob,
  BulkPodJobItemRow,
} from '@/types'

export const authApi = {
  getCurrentUser: (): Promise<ApiResponse<User>> =>
    apiClient.get('/api/auth/me'),

  getUsers: (params?: { role?: string; division?: string; limit?: number }): Promise<ApiResponse<User[]>> =>
    apiClient.get('/api/auth/users', { params }),

  getUserById: (id: string): Promise<ApiResponse<User>> =>
    apiClient.get(`/api/auth/users/${id}`),

  /** Same as loading session via IAM JWT + fingerprint (Seekho-aligned). */
  validateSession: (): Promise<ApiResponse<User>> =>
    apiClient.get('/api/auth/me').then((res) => res.data),

  forgotPassword: (email: string): Promise<{ status: string; message: string }> =>
    apiClient.post('/api/auth/forgot-password', { email }).then((res) => res.data),
}

export const approvalsApi = {
  getApprovals: (params?: {
    status?: string
    type?: string
    dateFrom?: string
    dateTo?: string
    customerCode?: string
    transporterCode?: string
    documentNo?: string
    lrNo?: string
    vehicleNo?: string
    customerName?: string
    transporterName?: string
    fromLocation?: string
    toLocation?: string
    limit?: number
    offset?: number
  }): Promise<ApiResponse<Approval[]>> => apiClient.get('/api/approvals', { params }),

  getApprovalById: (id: number): Promise<ApiResponse<Approval>> =>
    apiClient.get(`/api/approvals/${id}`),

  createApproval: (formData: FormData): Promise<ApiResponse<Approval>> =>
    apiClient.post('/api/approvals', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  updateApproval: (id: number, data: UpdateApprovalRequest): Promise<ApiResponse<Approval>> =>
    apiClient.put(`/api/approvals/${id}`, data),

  bulkUpdateApprovals: (data: BulkUpdateRequest): Promise<ApiResponse<any[]>> =>
    apiClient.post('/api/approvals/bulkUpdate', data),

  deleteApproval: (id: number): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/approvals/${id}`),
}

export const documentsApi = {
  listDocuments: (params?: {
    documentNo?: string
    status?: string
    type?: string
    dateFrom?: string
    dateTo?: string
    limit?: number
    offset?: number
  }): Promise<ApiResponse<DocumentSummary[]>> => apiClient.get('/api/documents', { params }),

  getHistory: (documentNo: number | string, params?: { type?: string }): Promise<ApiResponse<Approval[]>> =>
    apiClient.get(`/api/documents/${documentNo}/history`, { params }),
}

export const sapApi = {
  getDocument: (documentNo: number, type: string): Promise<ApiResponse<SapDocument>> =>
    apiClient.post('/api/get', { documentNo, type }, { timeout: API_SAP_TIMEOUT_MS }),

  /** POD-WEB compatible fetch (Request_Data payload; expects data.Response_Data) */
  getDocumentLegacy: (payload: { Request_Data: { DocNo: string; DocType: string } }): Promise<ApiResponse<any>> =>
    apiClient.post('/api/getDocument', payload, { timeout: API_SAP_TIMEOUT_MS }),

  fetchTransporterDocuments: (
    transporterCode: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<ApiResponse<SapDocument[]>> =>
    apiClient.post(
      '/api/Fetch_TransporterDocs',
      { transporterCode, dateFrom, dateTo },
      { timeout: API_SAP_TIMEOUT_MS }
    ),

  /** Legacy POD-WEB wrapper (Request_Data + DD/MM dates handled server-side). */
  fetchTransporterDocumentsLegacy: (payload: {
    Request_Data: {
      DocType?: string
      TransporterCode: string
      FromDate: string
      ToDate: string
    }
  }): Promise<ApiResponse<unknown>> =>
    apiClient.post('/api/Fetch_TransporterDocs', payload, { timeout: API_SAP_TIMEOUT_MS }),

  checkHealth: (): Promise<ApiResponse<{ healthy: boolean; message: string }>> =>
    apiClient.get('/api/sap/health'),

  getDocumentTypes: (): Promise<ApiResponse<DocumentType[]>> =>
    apiClient.get('/api/sap/document-types').then((res: any) => {
      const payload = res?.data
      if (payload) {
        let list: any[] = []
        if (Array.isArray(payload)) {
          list = payload
        } else if (Array.isArray(payload.data)) {
          list = payload.data
        }
        list.forEach((item: any) => {
          if (item.code === 'ALT') {
            item.name = 'Allot(ALT)'
          } else if (item.code === 'DHR') {
            item.name = 'Dehire(DHR)'
          } else if (item.code === 'GRN') {
            item.name = 'Goods Receipt Note(GRN)'
          } else if (item.code === 'REL_OUT') {
            item.name = 'Relocation(RELO)'
          } else if (item.code === 'TS') {
            item.name = 'Trading Sale(TS)'
          }
        })

        if (!list.some((item: any) => item.code === 'job_work')) {
          list.push({ code: 'job_work', name: 'job_work', description: 'Job work documents', disabled: true })
        }
        if (!list.some((item: any) => item.code === 'trading_cell')) {
          list.push({ code: 'trading_cell', name: 'trading_cell', description: 'Trading cell documents', disabled: true })
        }
      }
      return res
    }),

  getEndpoints: (): Promise<ApiResponse<SapInfo>> =>
    apiClient.get('/api/sap/endpoints'),
}

export const reportsApi = {
  exportToExcel: async (params?: {
    status?: string
    type?: string
    customerCode?: string
    transporterCode?: string
    dateFrom?: string
    dateTo?: string
    documentNo?: string
    lrNo?: string
    vehicleNo?: string
    customerName?: string
    transporterName?: string
    fromLocation?: string
    toLocation?: string
  }): Promise<Blob> => {
    const res = await apiClient.get<Blob>('/api/reports/export', {
      params,
      responseType: 'blob',
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    })
    return res.data
  },

  getStatistics: (params?: {
    dateFrom?: string
    dateTo?: string
    type?: string
    customerCode?: string
    transporterCode?: string
    documentNo?: string
    lrNo?: string
    vehicleNo?: string
    customerName?: string
    transporterName?: string
    fromLocation?: string
    toLocation?: string
  }): Promise<ApiResponse<Statistics>> =>
    apiClient.get('/api/reports/statistics', { params }),

  getDashboard: (params?: { days?: number }): Promise<ApiResponse<DashboardData>> =>
    apiClient.get('/api/reports/dashboard', { params }),
}

export const bulkPodApi = {
  createJob: (payload: {
    driveUrl: string
    documentType: string
    delayMs?: number
  }): Promise<ApiResponse<BulkPodJob>> => apiClient.post('/api/bulk-pod/jobs', payload),

  getJob: (id: number): Promise<ApiResponse<BulkPodJob>> => apiClient.get(`/api/bulk-pod/jobs/${id}`),

  getJobItems: (
    id: number,
    params?: { limit?: number; offset?: number; status?: string }
  ): Promise<ApiResponse<BulkPodJobItemRow[]>> => apiClient.get(`/api/bulk-pod/jobs/${id}/items`, { params }),

  exportJobItems: async (id: number, params?: { status?: string }): Promise<Blob> => {
    const res = await apiClient.get(`/api/bulk-pod/jobs/${id}/export`, {
      params,
      responseType: 'blob',
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    })
    return res.data as Blob
  },

  rollbackPreview: (id: number): Promise<ApiResponse<import('@/types').BulkUploadRollbackPreview>> =>
    apiClient.get(`/api/bulk-pod/jobs/${id}/rollback-preview`, {
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    }),

  rollbackJob: (id: number): Promise<ApiResponse<unknown>> =>
    apiClient.post(`/api/bulk-pod/jobs/${id}/rollback`, { confirm: true }, {
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    }),
}

export const podBulkUploadApi = {
  storeFiles: (payload: {
    documentType: string
    batchId?: string
    files: Array<{ document_no: string; file_name: string; file_url?: string | null }>
  }): Promise<ApiResponse<{ storedCount: number; batchId?: string | null; rejected?: Array<{ file_name: string; reason: string }> }>> =>
    apiClient.post('/api/pod/bulk-upload/store-files', payload, {
      timeout: API_BULK_METADATA_TIMEOUT_MS,
    }),

  processQueue: (payload?: { batchId?: string; retryRateLimited?: boolean }): Promise<ApiResponse<{
    jobStarted: boolean
    alreadyRunning?: boolean
    batchId?: string | null
    message?: string
  }>> =>
    apiClient.post('/api/pod/bulk-upload/process-queue', payload ?? {}),

  queueStatus: (params?: { batchId?: string }): Promise<ApiResponse<{
    job: {
      running: boolean
      startedAt: string | null
      batchId: string | null
      done: boolean
      rolledBack?: boolean
      summary: {
        totalProcessed: number
        approvedUpdated: number
        approvedInserted: number
        skippedApproved: number
        failed: number
        attachmentsCreated?: number
      } | null
      error: string | null
      coolingDown?: boolean
      cooldownEndsAt?: string | null
    }
    queue: {
      PENDING: number
      PROCESSING: number
      COMPLETED: number
      FAILED: number
      SKIPPED: number
      ROLLED_BACK?: number
    }
    outcomeCounts?: Record<string, number>
    batchSummary?: import('@/types').BulkUploadBatchSummary | null
  }>> =>
    apiClient.get('/api/pod/bulk-upload/queue-status', { params }),

  getQueueItems: (
    params: { batchId: string; limit?: number; offset?: number; status?: string; outcome?: string; search?: string }
  ): Promise<ApiResponse<import('@/types').PodBulkUploadQueueItemRow[]> & { total?: number }> =>
    apiClient.get('/api/pod/bulk-upload/queue-items', { params }),

  exportQueueItems: async (
    params: { batchId: string; status?: string; outcome?: string; search?: string }
  ): Promise<Blob> => {
    const res = await apiClient.get('/api/pod/bulk-upload/queue-export', {
      params,
      responseType: 'blob',
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    })
    return res.data as Blob
  },

  rollbackPreview: (batchId: string): Promise<ApiResponse<import('@/types').BulkUploadRollbackPreview>> =>
    apiClient.get(`/api/pod/bulk-upload/batches/${batchId}/rollback-preview`, {
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    }),

  rollbackBatch: (batchId: string): Promise<ApiResponse<unknown>> =>
    apiClient.post(`/api/pod/bulk-upload/batches/${batchId}/rollback`, { confirm: true }, {
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    }),

  checkExisting: (payload: {
    documentType: string
    fileNames: string[]
  }): Promise<ApiResponse<{ existing: string[] }>> =>
    apiClient.post('/api/pod/bulk-upload/check-existing', payload, {
      timeout: API_BULK_METADATA_TIMEOUT_MS,
    }),

  checkResume: (payload: {
    documentType: string
    fileNames: string[]
  }): Promise<
    ApiResponse<import('@/utils/bulkUploadResume').BulkUploadResumeStatus>
  > =>
    apiClient.post('/api/pod/bulk-upload/check-resume', payload, {
      timeout: API_BULK_METADATA_TIMEOUT_MS,
    }),

  processDocument: (payload: {
    batchId: string
    documentNo: string
    documentType: string
    files?: Array<{ document_no: string; file_name: string; file_url?: string | null }>
  }): Promise<
    ApiResponse<{
      documentNo: string
      skipped?: boolean
      outcome?: string | null
      approvalId?: number | null
      failed?: boolean
      status?: string
      error?: string
      rateLimitHit?: boolean
    }>
  > =>
    apiClient.post('/api/pod/bulk-upload/process-document', payload, {
      timeout: API_SAP_TIMEOUT_MS,
    }),

  assignBatch: (payload: {
    documentType: string
    batchId: string
    fileNames: string[]
  }): Promise<ApiResponse<{ assignedCount: number; batchId: string }>> =>
    apiClient.post('/api/pod/bulk-upload/assign-batch', payload),

  historyStats: (): Promise<ApiResponse<import('@/types').BulkUploadHistoryStats>> =>
    apiClient.get('/api/pod/bulk-upload/history-stats'),
}

export const itAdminApi = {
  getApprovalsSyncPreview: (params?: {
    sourceDatabase?: string
  }): Promise<ApiResponse<import('@/types').ApprovalsSyncPreview>> =>
    apiClient
      .get('/api/it-admin/approvals-sync/preview', { params })
      .then((res) => res.data),

  getApprovalsSyncJob: (jobId: string): Promise<ApiResponse<import('@/types').ApprovalsSyncJob>> =>
    apiClient.get(`/api/it-admin/approvals-sync/jobs/${jobId}`).then((res) => res.data),

  runApprovalsSync: async (
    payload: {
      mode: 'merge' | 'replace'
      sourceDatabase: string
      confirmReplace?: boolean
    },
    onProgress?: (progress: import('@/types').ApprovalsSyncJobProgress) => void
  ): Promise<ApiResponse<import('@/types').ApprovalsSyncResult>> => {
    const SYNC_POLL_MS = 2000
    const SYNC_MAX_WAIT_MS = 45 * 60 * 1000

    const startRes = await apiClient
      .post(
        '/api/it-admin/approvals-sync/run',
        { ...payload, async: true },
        { timeout: API_LONG_RUNNING_TIMEOUT_MS }
      )
      .then((res) => res.data as ApiResponse<{ jobId: string; status: string }>)

    if (!startRes.success || !startRes.data?.jobId) {
      return {
        success: false,
        error: startRes.error || startRes.message || 'Failed to start approvals sync',
      }
    }

    const jobId = startRes.data.jobId
    const deadline = Date.now() + SYNC_MAX_WAIT_MS

    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, SYNC_POLL_MS))

      const statusRes = await apiClient
        .get(`/api/it-admin/approvals-sync/jobs/${jobId}`, { timeout: API_LONG_RUNNING_TIMEOUT_MS })
        .then((res) => res.data as ApiResponse<import('@/types').ApprovalsSyncJob>)

      if (!statusRes.success || !statusRes.data) {
        return {
          success: false,
          error: statusRes.error || statusRes.message || 'Failed to load sync status',
        }
      }

      const job = statusRes.data
      if (job.progress && onProgress) onProgress(job.progress)

      if (job.status === 'completed' && job.result) {
        return { success: true, data: job.result }
      }

      if (job.status === 'failed') {
        return { success: false, error: job.error || 'Approvals sync failed' }
      }
    }

    return {
      success: false,
      error: 'Approvals sync timed out while waiting for completion',
    }
  },
}

export const filesApi = {
  uploadFile: (formData: FormData): Promise<ApiResponse<FileUploadResponse>> =>
    apiClient.post('/api/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: API_FILE_UPLOAD_TIMEOUT_MS,
    }),

  getSignedUrl: (key: string, expiresIn?: number): Promise<ApiResponse<{ url: string }>> =>
    apiClient.get(`/api/files/${key}/signed-url`, { params: { expiresIn } }),

  deleteFile: (key: string): Promise<ApiResponse<void>> =>
    apiClient.delete(`/api/files/${key}`),

  listFiles: (params?: { prefix?: string; maxKeys?: number }): Promise<ApiResponse<any[]>> =>
    apiClient.get('/api/files', { params }),

  checkHealth: (): Promise<ApiResponse<{ healthy: boolean; message: string }>> =>
    apiClient.get('/api/files/health'),
}

export const usersApi = {
  getApprovers: (): Promise<ApiResponse<User[]>> =>
    apiClient.get('/api/users/approvers'),

  getRequesters: (): Promise<ApiResponse<User[]>> =>
    apiClient.get('/api/users/requesters'),

  getTransporters: (): Promise<ApiResponse<User[]>> =>
    apiClient.get('/api/users/transporters'),
}
